import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  useTheme,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Percent as PercentIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import taxService from '../../services/taxService';
import { getCurrentUser } from '../../services/authService';
import PageTitle from '../../components/PageTitle';
import { useSnackbar } from 'notistack';

/**
 * Tax Rates management page component
 * @returns {JSX.Element} Taxes page
 */
const TaxesPage = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const user = getCurrentUser();
  const companyId = user?.companyId || 1; // Fallback to 1 for development

  // Set page title on component mount
  useEffect(() => {
    document.title = "Tax Rates";
  }, []);

  // State management
  const [taxes, setTaxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTax, setSelectedTax] = useState(null);
  const [taxToDelete, setTaxToDelete] = useState(null);
  const [formValues, setFormValues] = useState({
    name: '',
    rate: '',
    description: '',
    status: 'ACTIVE'
  });

  // Load tax rates - wrapped in useCallback
  const loadTaxes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await taxService.getAllTaxes({
        page,
        size: rowsPerPage,
        sort: 'name',
        direction: 'ASC'
      });

      // Add proper null checks before accessing properties
      if (response && response.content) {
        setTaxes(response.content);
        setTotalElements(response.totalElements || 0);
      } else {
        // If response format is different, handle it appropriately
        // It might be an array directly instead of a Page object
        setTaxes(Array.isArray(response) ? response : []);
        setTotalElements(Array.isArray(response) ? response.length : 0);
      }
    } catch (error) {
      console.error('Error loading tax rates:', error);
      window.alert('Failed to load tax rates: ' + (error.response?.data?.message || error.message || 'Unknown error'));
      // Set empty array to avoid further errors
      setTaxes([]);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  // Initial load
  useEffect(() => {
    loadTaxes();
  }, [loadTaxes]); // Depend only on loadTaxes which is memoized

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Dialog handlers
  const handleOpenDialog = (tax = null) => {
    if (tax) {
      setSelectedTax(tax);
      setFormValues({
        name: tax.name,
        rate: tax.rate,
        description: tax.description || '',
        status: tax.status || 'ACTIVE'
      });
    } else {
      setSelectedTax(null);
      setFormValues({
        name: '',
        rate: '',
        description: '',
        status: 'ACTIVE'
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedTax(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value
    });
  };

  // Form submission
  const handleSubmit = async () => {
    try {
      // Convert rate to decimal for storage
      const taxData = {
        name: formValues.name,
        rate: parseFloat(formValues.rate),
        description: formValues.description,
        status: formValues.status,
        companyId
      };

      if (selectedTax) {
        // Update existing tax
        await taxService.updateTax(selectedTax.id, taxData);
        enqueueSnackbar('Tax rate updated successfully', { variant: 'success' });
      } else {
        // Create new tax
        await taxService.createTax(taxData);
        enqueueSnackbar('Tax rate created successfully', { variant: 'success' });
      }

      // Close the dialog and refresh data
      handleCloseDialog();
      loadTaxes();
    } catch (error) {
      console.error('Error saving tax rate:', error);
      window.alert(
        `Failed to ${selectedTax ? 'update' : 'create'} tax rate: ${error.response?.data?.message || error.message}`
      );
    }
  };

  // Validation logic
  const isFormValid = Boolean(
    formValues.name &&
    (formValues.rate !== '' && formValues.rate !== null && formValues.rate !== undefined)
  );

  // Delete handlers
  const handleOpenDeleteDialog = (tax) => {
    setTaxToDelete(tax);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setTaxToDelete(null);
  };

  const handleDeleteTax = async () => {
    try {
      await taxService.deleteTax(taxToDelete.id);
      enqueueSnackbar('Tax rate deleted successfully', { variant: 'success' });
      handleCloseDeleteDialog();
      loadTaxes();
    } catch (error) {
      console.error('Error deleting tax rate:', error);
      window.alert(
        `Failed to delete tax rate: ${error.response?.data?.message || error.message}`
      );
    }
  };

  return (
    <Layout>
      <PageTitle title="Tax Rates" />
      <Box sx={{ width: '100%', p: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            Tax Rates
          </Typography>

          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Tax Rate
          </Button>
        </Box>

        {/* Tax Rates Table */}
        <Paper elevation={0} sx={{
          p: 0,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
        }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Rate</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : taxes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No tax rates found
                    </TableCell>
                  </TableRow>
                ) : (
                  taxes.map((tax) => (
                    <TableRow key={tax.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PercentIcon sx={{ mr: 1, color: 'primary.main' }} />
                          {tax.name}
                        </Box>
                      </TableCell>
                      <TableCell>{tax.rate}%</TableCell>
                      <TableCell>{tax.description || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={tax.status === 'ACTIVE' ? 'Active' : 'Inactive'}
                          color={tax.status === 'ACTIVE' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex' }}>
                          <Tooltip title="Edit">
                            <IconButton
                              color="info"
                              onClick={() => handleOpenDialog(tax)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              color="error"
                              onClick={() => handleOpenDeleteDialog(tax)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalElements}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Box>

      {/* Add/Edit Tax Rate Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedTax ? 'Edit Tax Rate' : 'Add New Tax Rate'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Tax Name"
              name="name"
              value={formValues.name}
              onChange={handleInputChange}
              margin="normal"
              required
              helperText="e.g., VAT, GST, Sales Tax"
            />

            <TextField
              fullWidth
              label="Rate (%)"
              name="rate"
              type="number"
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              value={formValues.rate}
              onChange={handleInputChange}
              margin="normal"
              required
              helperText="Tax rate percentage (e.g., 18 for 18%)"
            />

            <FormControl fullWidth margin="normal">
              <InputLabel id="status-label">Status</InputLabel>
              <Select
                labelId="status-label"
                name="status"
                value={formValues.status}
                onChange={handleInputChange}
                label="Status"
              >
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="INACTIVE">Inactive</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Description"
              name="description"
              value={formValues.description}
              onChange={handleInputChange}
              margin="normal"
              multiline
              rows={3}
              helperText="Optional description of the tax rate"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={!isFormValid}
          >
            {selectedTax ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the tax rate <strong>{taxToDelete?.name}</strong>?
            This action cannot be undone.
          </Typography>
          <Typography variant="caption" color="error" sx={{ mt: 2, display: 'block' }}>
            Note: Tax rates that are in use by products cannot be deleted. You must reassign products to a different tax rate first.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button
            onClick={handleDeleteTax}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default TaxesPage; 
